import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };

const statusColors = {
  assigned: { bg: '#fff3cd', color: '#856404' },
  in_progress: { bg: '#d1ecf1', color: '#0c5460' },
  completed: { bg: '#d4edda', color: '#155724' }
};

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function AssignExam() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({ student_id: '', exam_id: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true); setError('');
    try {
      const [studRes, examRes, assignRes] = await Promise.all([
        fetch(`${API_BASE}/admin/students`).then(r => r.json()),
        fetch(`${API_BASE}/admin/exams`).then(r => r.json()),
        fetch(`${API_BASE}/admin/assignments`).then(r => r.json())
      ]);
      setStudents(studRes); setExams(examRes); setAssignments(assignRes);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.exam_id) return setMessage('❌ Select both student and exam');
    
    try {
      const res = await fetch(`${API_BASE}/admin/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to assign');
      setMessage('✅ Exam assigned successfully!');
      setFormData({ student_id: '', exam_id: '' });
      fetchData();
    } catch (err) { setMessage(`❌ ${err.message}`); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Assign Exams</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}
      {message && <div style={{ padding: 10, background: message.includes('✅') ? '#d4edda' : '#f8d7da', color: message.includes('✅') ? '#155724' : '#721c24', borderRadius: 6, marginBottom: 15 }}>{message}</div>}

      {/* Assignment Form */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 25 }}>
        <h3 style={{ marginTop: 0 }}>New Assignment</h3>
        <form onSubmit={handleAssign} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 15, alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Student:</label>
            <select style={inputStyle} name="student_id" value={formData.student_id} onChange={handleChange} required>
              <option value="">Select Student...</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.student_id} - {s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Exam:</label>
            <select style={inputStyle} name="exam_id" value={formData.exam_id} onChange={handleChange} required>
              <option value="">Select Exam...</option>
              {exams.map(e => <option key={e.id} value={e.id}>{e.exam_name} ({e.exam_topic})</option>)}
            </select>
          </div>
          <button type="submit" style={{ ...btnPrimary, height: 42 }}>Assign</button>
        </form>
      </div>

      {/* Assignments Table */}
      <h3 style={{ marginBottom: 15 }}>Current Assignments</h3>
      {loading ? <p>Loading assignments...</p> : assignments.length === 0 ? <p style={{ color: '#666' }}>No assignments yet.</p> : (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #ddd', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1.2fr 1.5fr 1.5fr', gap: 10, padding: '10px 15px', background: '#e9ecef', fontWeight: 600, fontSize: 14 }}>
            <div>Student</div><div>Exam</div><div>Status</div><div>Started</div><div>Completed</div><div>Assigned</div>
          </div>
          {/* Rows */}
          {assignments.map(a => {
            const statusStyle = statusColors[a.status] || { bg: '#f8f9fa', color: '#666' };
            return (
              <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.2fr 1.5fr 1.5fr 1.5fr', gap: 10, padding: '12px 15px', borderBottom: '1px solid #eee', alignItems: 'center' }}>
                <div style={{ fontWeight: 500 }}>{a.student_id} - {a.first_name} {a.last_name}</div>
                <div>{a.exam_name} <span style={{ color: '#888', fontSize: 12 }}>({a.exam_topic})</span></div>
                <div>
                  <span style={{ padding: '3px 10px', background: statusStyle.bg, color: statusStyle.color, borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                    {a.status.replace('_', ' ')}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#666' }}>{formatDate(a.started_at)}</div>
                <div style={{ fontSize: 13, color: '#666' }}>{formatDate(a.completed_at)}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{formatDate(a.created_at)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}